import { ReactNode, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { AppSidebar } from "@/components/app-sidebar";
import { GamesPortalRoot } from "@/features/games-portal";
import { useSlashCommandsLocationCleanup } from "@/features/slash-commands/hooks/use-slash-commands-location-cleanup";
import { GlobalHeader } from "@/components/layout/global-header";
import { MobileBreadcrumbBar } from "@/features/breadcrumb";
import { ChatDock, ChatDockToggle } from "@/features/chat-dock";
import { TemporaryChatModal } from "@/features/temporary-chat";
import { useFloatingHeader } from "@/components/layout/use-floating-header";
import { GradientScrollbar } from "@/components/ui/gradient-scrollbar";

interface MainLayoutProps {
  children: ReactNode;
}

/**
 * Üç panelli kabuk: sol sidebar | içerik | sağ chat dock.
 * Dock yalnızca chat'i açık bir modülde render olur (bkz. useChatDock);
 * diğer sayfalarda yerleşim iki panel olarak kalır.
 */
const MainLayout = ({ children }: MainLayoutProps) => {
  // 🎯 Location değiştiğinde slash command menu'yu kapat
  useSlashCommandsLocationCleanup();

  // İçerik kaydırıcısı route'lar arasında ortak; yeni sayfaya geçişte (PUSH /
  // REPLACE) en üste al. Geri/ileri (POP) tarayıcı davranışına bırakılır.
  const scrollRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const floatingHeader = useFloatingHeader();
  useLayoutEffect(() => {
    if (navigationType !== "POP") scrollRef.current?.scrollTo({ top: 0 });
  }, [pathname, navigationType]);

  // Yüzen header için kaydırma durumu: React state yok, main'e data attribute
  // yazılır; header CSS ile okuyup cam zeminini geri getirir
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const main = mainRef.current;
    if (!el || !main) return;
    const sync = () => main.toggleAttribute("data-scrolled", el.scrollTop > 8);
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    return () => el.removeEventListener("scroll", sync);
  }, [pathname]);

  return (
    <div className="relative h-full w-full bg-bg-primary dark:bg-gray-900 text-foreground">
      {/* Layout Container - Sidebar + Main Content + Chat Dock */}
      <div className="flex h-full w-full">
        {/* Sidebar - Global, tüm sayfalarda */}
        <AppSidebar />

        {/* Main Content Area */}
        <main
          ref={mainRef}
          data-floating-header={floatingHeader || undefined}
          className="flex-1 min-w-0 h-full overflow-hidden relative flex flex-col pt-0"
        >
          <GlobalHeader />
          {/* Mobil: breadcrumb header'ın altında tam genişlik şerit (md+ render olmaz) */}
          <MobileBreadcrumbBar />
          {/* Native çubuk gizli; GradientScrollbar aynı kaydırıcıyı sağ kenarda çizer */}
          <div className="relative min-h-0 flex-1">
            <div
              ref={scrollRef}
              data-app-scroller
              className="h-full w-full overflow-x-hidden overflow-y-auto scrollbar-hide"
            >
              {children}
            </div>
            <GradientScrollbar scrollRef={scrollRef} />
          </div>
        </main>

        {/* Sağ chat dock — md+ kolon, mobilde sheet */}
        <ChatDock />
      </div>
      {/* Dock kapalıyken sağ kenar tutamacı */}
      <ChatDockToggle />
      {/* Geçici sohbet bilgi modalı — zero props, mod açılınca bir kez görünür */}
      <TemporaryChatModal />
      <GamesPortalRoot />
    </div>
  );
};

export default MainLayout;
